/**
 * Approval Detail Modal
 * Shows full details of a pending change with before/after comparison
 * For contract approvals, shows rich preview with contract summary,
 * impact metrics, and amortization schedule (like Rate Change Impact Assessment)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Tab,
  Tabs,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Verified as VerifiedIcon,
  HelpOutline as QueryIcon,
  Undo as ReverseIcon,
  History as HistoryIcon,
  CompareArrows as CompareIcon,
  AttachFile as AttachmentIcon,
  AccountBalance as AccountBalanceIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Reply as ReplyIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Functions as FunctionsIcon,
} from '@mui/icons-material';
import { format, differenceInYears, differenceInDays, addYears } from 'date-fns';
import { apiGet, apiPost, apiUpload } from '../../utils/apiClient';
// T3-121: label a payment carry-forward distinguishably from an ordinary schedule override --
// they share a change_type on purpose, so the existing executor applies them. Display only.
import { changeTypeLabel } from '../../utils/paymentHelpers';
import { formatNumber, formatRatePct, formatShortDate, formatRateChangeBasis, formatBrokerFee } from '../../utils/formatters';
import { getNarrationFlowType as getFlowType, getNarrationColor, getNarrationFlowLabel, isAdjustmentNarration, formatNarrationAmount, getNarrationRowSx } from '../../utils/narration';
import useCurrencyFormatter from '../../hooks/useCurrencyFormatter';
import { exportAmortizationSchedule } from '../../utils/excelExport';
import logger from '../../utils/logger';
import TabPanel from '../common/TabPanel';
import FileDropZone from '../common/FileDropZone';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import useDocumentSummaries from '../../hooks/useDocumentSummaries';
import useDocumentActions from '../../hooks/useDocumentActions';
import DocumentPreviewDialog from '../documents/DocumentPreviewDialog';
import DocumentPreviewPane from '../documents/DocumentPreviewPane';
import { groupAmortizationSchedule } from '../../utils/groupAmortizationSchedule';
import ScheduleComparisonTable from '../contracts/ScheduleComparisonTable';
import CalculationDetailPopover from '../contracts/CalculationDetailPopover';
import ImpactSummaryCards from '../contracts/ImpactSummaryCards';
import { buildComparisonRows, isPeriodAccrual } from '../../utils/scheduleComparison';
import {
  isTerminalBalanceZero,
  getTerminalBalance,
  SCHEDULE_TERMINAL_BALANCE_TOLERANCE,
} from '../../utils/scheduleValidation';

// Friendly display label for a document_type code returned by useDocumentSummaries.
// Covers the seeded submission document types (T3-038/039); unknown codes are title-cased,
// and a missing code falls back to a generic "Document" label.
const DOCUMENT_TYPE_LABELS = {
  contract_signed: 'Signed Contract',
  contract: 'Signed Contract',
  investor_certificate: 'Investor Certificate',
  kyc: 'KYC',
  pop: 'Proof of Payment',
  resolution: 'Resolution',
  statement: 'Statement',
  interest_letter: 'Interest Letter',
  other: 'Other Document',
};
const documentTypeLabel = (code) => {
  if (!code) return 'Document';
  const key = code.toLowerCase();
  if (DOCUMENT_TYPE_LABELS[key]) return DOCUMENT_TYPE_LABELS[key];
  // Title-case an unknown snake/kebab code (e.g. "board_minutes" → "Board Minutes").
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Shared fixed-height header band for the Contract-Assessment left column heading and the
// docked preview-pane header, so their text tops pin to the same Y (deterministic alignment
// regardless of the pane Paper's border). Applied to a Box that wraps a single subtitle2 line.
const PANE_HEADING_SX = {
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
};

// Status chip styling
const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'pending_review': return 'info';
    case 'approved': return 'success';
    case 'verified': return 'success';
    case 'rejected': return 'error';
    case 'queried': return 'secondary';
    case 'reversed': return 'error';
    case 'cancelled': return 'default';
    case 'executed': return 'success';
    default: return 'default';
  }
};

const headerCellSx = { backgroundColor: '#f5f5f5', fontWeight: 'bold' };

function ApprovalDetailModal({ open, onClose, item, onAction, permissions }) {
  const { currentUserEmail, currentUserId } = usePermissions();
  const { isWorkflowDocumentRequired } = useTenant();
  const { formatCurrency: formatCurrencyDp, dp } = useCurrencyFormatter();
  const verificationDocRequired = isWorkflowDocumentRequired('approval_verification');
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [canApproveData, setCanApproveData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  // Investor list — multi-investor contracts have a NULL scalar investor_name
  // (investors live in the contract_investors junction), so the display name(s)
  // come from GET /contracts/:id/investors, falling back to the scalar.
  const [contractInvestors, setContractInvestors] = useState([]);
  // Per-investor summary panel (multi-investor contracts) — collapsible, collapsed by default.
  const [investorSummaryOpen, setInvestorSummaryOpen] = useState(false);
  // Which amortization period groups are expanded to show per-investor sub-rows (multi-investor).
  const [expandedScheduleGroups, setExpandedScheduleGroups] = useState({});
  const toggleScheduleGroup = useCallback((key) => {
    setExpandedScheduleGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  // Calculation Detail (Σ) popover — mirrors AmortizationScheduleTable. The approval schedule is
  // the frozen submitted snapshot (T118); since T3-072 it carries each row's id, so the popover
  // fetches the persisted calc audit via /amortization/entry/:id/audit. Snapshots don't carry an
  // inline calc_detail, so we drive the popover by entryId only.
  const [auditAnchorEl, setAuditAnchorEl] = useState(null);
  const [auditEntryId, setAuditEntryId] = useState(null);
  const handleAuditClick = useCallback((event, entry) => {
    if (!entry?.id) return;
    setAuditAnchorEl(event.currentTarget);
    setAuditEntryId(entry.id);
  }, []);
  const handleAuditClose = useCallback(() => {
    setAuditAnchorEl(null);
    setAuditEntryId(null);
  }, []);
  // Match the docked preview pane's height to the left contract-detail column so their bottoms
  // align. The pane can't drive the row height (its PDF would stretch it), so we measure the
  // left column with a ResizeObserver and apply that pixel height to the pane.
  const leftColumnRef = useRef(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState(null);
  const [adhocCapitalEvents, setAdhocCapitalEvents] = useState([]);
  const [adhocInterestPayments, setAdhocInterestPayments] = useState([]);
  const [finalisationSummary, setFinalisationSummary] = useState(null);
  // Document preview & download (shared hook)
  const docActions = useDocumentActions();

  // The terminal balance is read from the engine-written, persisted outstanding_balance on
  // the last schedule row — NEVER recomputed client-side. This is the single source of truth
  // shared with the backend approve guard (assertTerminalBalanceZero) and the contract detail
  // page; a prior client-side recompute double-counted WHT on accrual rows and produced a
  // phantom non-zero residual that wrongly blocked approval.
  const terminalBalanceOk = isTerminalBalanceZero(scheduleData?.schedule);
  const isContractApprovalWithSchedule =
    item?.change_type === 'contract_approval' &&
    Array.isArray(scheduleData?.schedule) &&
    scheduleData.schedule.length > 0;
  const approveBlockedForBalance = isContractApprovalWithSchedule && !terminalBalanceOk;
  const terminalBalanceForDisplay = getTerminalBalance(scheduleData?.schedule);

  // Resolve proof document IDs to filenames (detail view)
  const detailDocIds = detailData?.proof_document_ids;
  const { documents: proofDocs, reload: reloadProofDocs } = useDocumentSummaries(detailDocIds);

  // Resolve proof document IDs to filenames (list-level chips)
  const itemDocIds = item?.proof_document_ids;
  const { documents: itemProofDocs } = useDocumentSummaries(itemDocIds);

  useEffect(() => {
    if (open && item) {
      loadDetails();
      checkCanApprove();
    } else {
      // Reset state when modal closes
      setCanApproveData(null);
      setContractData(null);
      setScheduleData(null);
      setContractInvestors([]);
      setAdhocCapitalEvents([]);
      setAdhocInterestPayments([]);
      setFinalisationSummary(null);
      setAuditAnchorEl(null);
      setAuditEntryId(null);
      docActions.closePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDetails and checkCanApprove are stable functions that run when modal opens
  }, [open, item]);

  // Default the docked preview pane to the signed contract for contract approvals, so the
  // approver sees the contract immediately. Picks the doc tagged document_type 'contract_signed'
  // (case-insensitive; tolerates legacy 'CONTRACT'/'contract' seeds), falling back to the first
  // proof doc (the submit SP already orders them contract-first). Depends on proofDocs because the
  // document_type is only known once the batch-summary resolves. Guarded on !previewDocId so it
  // fires once per open and never clobbers a manual chip switch; the close-branch clears preview
  // state, re-arming this for the next open.
  useEffect(() => {
    if (!open) return;
    if (item?.change_type !== 'contract_approval') return;
    if (docActions.previewDocId) return;
    if (!Array.isArray(proofDocs) || proofDocs.length === 0) return;
    const isContractType = (t) => ['contract_signed', 'contract'].includes((t || '').toLowerCase());
    const contractDoc = proofDocs.find((d) => isContractType(d.document_type)) || proofDocs[0];
    if (contractDoc?.id) {
      docActions.openPreview(contractDoc.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openPreview is stable; guarded on previewDocId to fire once and preserve manual selection
  }, [open, item, proofDocs]);

  // Callback ref that observes the left contract-detail column and mirrors its height onto the
  // docked preview pane, so the two columns' bottoms align. A ResizeObserver keeps it in sync as
  // the left content changes (e.g. multi-currency rows, broker/collateral card, investor fetch).
  const leftColumnObserver = useRef(null);
  const setLeftColumnRef = useCallback((node) => {
    if (leftColumnObserver.current) {
      leftColumnObserver.current.disconnect();
      leftColumnObserver.current = null;
    }
    leftColumnRef.current = node;
    if (node && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        setLeftColumnHeight(node.offsetHeight || null);
      });
      ro.observe(node);
      leftColumnObserver.current = ro;
      setLeftColumnHeight(node.offsetHeight || null);
    }
  }, []);

  const loadDetails = async () => {
    if (!item) return;
    setLoading(true);
    try {
      const response = await apiGet(`/approvals/${item.id}`);
      let detail = null;
      if (response.success) {
        detail = response.data;
        setDetailData(response.data);
        if (response.data.history) {
          setHistory(response.data.history);
        }
      }

      // The contract approval is validated against the SUBMITTED schedule snapshot,
      // captured at submit time in after_snapshot.schedule (T118). This makes approval
      // immutable against any later schedule regeneration and avoids any client recompute.
      // Contracts submitted BEFORE T118 have no snapshot array → fall back to the live schedule.
      const submittedSchedule = Array.isArray(detail?.after_snapshot?.schedule)
        ? detail.after_snapshot.schedule
        : null;

      // For contract approvals, fetch full contract details, schedule, investors, and adhoc events
      if (item.change_type === 'contract_approval' && item.entity_id) {
        const [contractRes, scheduleRes, investorsRes, adhocCapitalRes, adhocInterestRes] = await Promise.allSettled([
          apiGet(`/contracts/${item.entity_id}`),
          submittedSchedule ? Promise.resolve(null) : apiGet(`/amortization/contract/${item.entity_id}`),
          apiGet(`/contracts/${item.entity_id}/investors`),
          apiGet(`/contracts/${item.entity_id}/adhoc-capital`),
          apiGet(`/contracts/${item.entity_id}/adhoc-interest-payments`),
        ]);
        if (contractRes.status === 'fulfilled' && contractRes.value?.success) {
          setContractData(contractRes.value.data);
        }
        if (submittedSchedule) {
          // Use the immutable submitted snapshot — the source of truth for approval.
          setScheduleData({ schedule: submittedSchedule });
        } else if (scheduleRes.status === 'fulfilled' && scheduleRes.value?.success) {
          setScheduleData(scheduleRes.value.data);
        }
        // Investor name(s): prefer the junction list; multi-investor contracts have no
        // scalar investor_name, so the Investor row would otherwise show blank.
        if (investorsRes.status === 'fulfilled' && Array.isArray(investorsRes.value?.data)) {
          setContractInvestors(investorsRes.value.data);
        }
        if (adhocCapitalRes.status === 'fulfilled' && adhocCapitalRes.value?.success) {
          setAdhocCapitalEvents(adhocCapitalRes.value.data || []);
        }
        if (adhocInterestRes.status === 'fulfilled' && adhocInterestRes.value?.success) {
          setAdhocInterestPayments(adhocInterestRes.value.data || []);
        }
      }

      // For finalisations, fetch the per-investor financial summary so the
      // After panel can show projected reserve/payout instead of "No projected state".
      if (item.change_type === 'contract_finalisation' && item.entity_id) {
        const finalisationDate = item.change_data?.finalisation_date;
        if (finalisationDate) {
          try {
            const summaryRes = await apiGet(
              `/contracts/${item.entity_id}/finalisation-summary?finalisation_date=${finalisationDate}`
            );
            if (summaryRes?.success) {
              setFinalisationSummary(summaryRes.data || []);
            }
          } catch (err) {
            // Non-fatal: fall back to the basic Before/After view.
            logger.error('Error loading finalisation summary', { message: err.message });
          }
        }
      }
    } catch (error) {
      logger.error('Error loading approval details', { message: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Check if current user can approve this item (creator/approver separation).
  // Runs for ALL change types — the server (fn_pending_changes_can_approve) applies
  // the contract-creator rule to contract_approval and a universal requested_by rule
  // to every other change type (adhoc_capital_*, adhoc_rate_change, ...).
  const checkCanApprove = async () => {
    if (!item) {
      setCanApproveData(null);
      return;
    }
    try {
      const response = await apiGet(`/approvals/${item.id}/can-approve`);
      if (response.success) {
        setCanApproveData(response.data);
      }
    } catch (error) {
      logger.error('Error checking approval eligibility', { message: error.message });
      // If API fails, check locally using the submitter (requested_by), falling
      // back to change_data.created_by (populated for contract_approval).
      const isContractApproval = item.change_type === 'contract_approval';
      const submittedBy = isContractApproval
        ? item.change_data?.created_by
        : (item.requested_by || item.change_data?.created_by);
      if (submittedBy && currentUserEmail &&
          submittedBy.toLowerCase().trim() === currentUserEmail.toLowerCase().trim()) {
        setCanApproveData({
          can_approve: false,
          reason: isContractApproval
            ? 'You cannot approve a contract you created'
            : 'You cannot approve a change you submitted',
          created_by: submittedBy,
        });
      }
    }
  };

  // Use fetched detail data for snapshots if available, fall back to item prop
  const currentItem = detailData || item;

  const formatDateTime = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd MMM yyyy HH:mm');
    } catch {
      return '-';
    }
  };

  const formatDateShort = (date) => {
    return formatShortDate(date);
  };


  // Determine which actions are available
  const getAvailableActions = () => {
    if (!item) return [];

    const category = item.category;
    const tabKey = `tab_${category === 'contract' ? 'contract_approvals' : 'verifications'}`;
    const isAdmin = permissions[tabKey] === 'admin';

    // Queried verifications: only the submitter can respond (no admin required)
    if (category === 'verification' && item.status === 'queried') {
      const requestedBy = item.requested_by?.toString().toLowerCase().trim();
      const isSubmitter = requestedBy && (
        requestedBy === currentUserEmail?.toLowerCase().trim() ||
        requestedBy === currentUserId?.toString()
      );
      return isSubmitter ? ['respond'] : [];
    }

    if (!isAdmin) return [];

    if (category === 'verification' && item.status === 'pending_review') {
      return ['verify', 'query', 'reverse'];
    }

    if ((category === 'contract' || category === 'system') && item.status === 'pending') {
      // Creator/approver separation: applies to ALL change types via canApproveData.
      if (canApproveData && !canApproveData.can_approve) {
        return ['reject'];
      }
      return ['approve', 'reject'];
    }

    return [];
  };

  const availableActions = getAvailableActions();

  // Summary row for contract details tables
  const SummaryRow = ({ label, value, highlight = false }) => (
    <TableRow>
      <TableCell
        component="th"
        scope="row"
        sx={{ fontWeight: 'medium', width: '40%', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ fontWeight: highlight ? 'bold' : 'normal', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}>
        {value || '-'}
      </TableCell>
    </TableRow>
  );

  // getFlowType/getNarrationColor/getNarrationFlowLabel are the shared helpers from
  // utils/narration (imported above), so this modal classifies + colours narration rows
  // identically to AmortizationScheduleTable (financial-consistency, single source of truth).
  // ADJ returns an sx (maroon) instead of a semantic color; render sites spread flowLabel.sx.
  const getFlowLabel = getNarrationFlowLabel;

  // Delegates to the shared formatter so the approval preview signs amounts exactly
  // like the schedule table (outflow red parens, inflow blue, negative accrual/neutral
  // -> default-colour parentheses).
  const formatFlowAmount = (value, flowType) => formatNarrationAmount(value, flowType);

  // Render a WHT cell, mirroring AmortizationScheduleTable.formatWhtForRow so the
  // approval preview matches the contract view. Non-zero gate (incl. negatives).
  // Accrual rows (Interest Accrual / Interest Float / Interest Accrual Adjustment)
  // show a plain accrual figure — positive plain, negative in default-colour parens;
  // payment rows show purple parens. isAdjustmentNarration matches both the old + new
  // label so frozen pre-rename snapshots still render right.
  const renderWhtCell = (whtAmount, narration) => {
    const wht = parseFloat(whtAmount) || 0;
    if (wht === 0) return '-';
    if (narration === 'Interest Accrual' || narration === 'Interest Float' || isAdjustmentNarration(narration)) {
      if (wht < 0) return `(${formatNumber(Math.abs(wht), 2)})`;
      return formatNumber(wht, 2);
    }
    return <span style={{ color: '#9c27b0' }}>({formatNumber(wht, 2)})</span>;
  };

  // Row/period Total = capital + NET interest (never interest + wht — that would
  // double-count WHT). Matches AmortizationScheduleTable's capital + netSafe.
  const rowTotal = ({ capital_amount, interest_amount, net_interest_amount }) => {
    const capital = parseFloat(capital_amount) || 0;
    const net = parseFloat(net_interest_amount);
    const netSafe = Number.isFinite(net) ? net : (parseFloat(interest_amount) || 0);
    return capital + netSafe;
  };

  // Render contract approval preview (rich layout like Rate Change Impact Assessment)
  const renderContractApprovalPreview = () => {
    const contract = contractData;
    const schedule = scheduleData?.schedule || [];
    const summary = scheduleData?.summary;
    const currencyCode = contract?.currency_code || '';
    const hasWhtData = schedule.some(entry => entry.gross_interest_amount || entry.wht_amount || entry.wht_rate_applied);

    // Multi-investor snapshots carry one row per investor per period. Group them into period
    // totals for display (presentation-only — sums the already-frozen per-investor values, no
    // recompute), using the SAME shared util as the contract wizard so the grouped totals match.
    // When rows carry no investor_id (single-investor, or pre-T3-055 snapshots) groups is null
    // and the flat table renders exactly as before.
    const { groups: scheduleGroups } = groupAmortizationSchedule(schedule);

    // The Outstanding Balance column renders each row's persisted outstanding_balance
    // (engine-written) — not a client recompute — so it matches the contract detail page
    // and the terminal-balance gate.

    // Interest totals are summed from the schedule rows — the SAME figures the amortization table
    // displays — rather than summary.*, which is absent on the T118 submitted-snapshot approval path
    // (setScheduleData({ schedule }) with no summary). A schedule carries BOTH Interest Accrual rows
    // AND Interest Payment rows (interest accrues monthly, pays quarterly — the payments settle exactly
    // what accrued), so summing every row double-counts. Count ONLY the payment (outflow) rows —
    // matching the amortization table's Gross Interest / WHT / Net Interest cards, the WHT breakdown,
    // and the API scheduleTotals helper (all payment/outflow-based). Accumulate gross, WHT and net in
    // one pass so the Contract Details card BALANCES: Gross − WHT = Net (net uses the schedule's own
    // interest_amount, which is already gross-up-aware — for a gross-up investor WHT is a company
    // expense and net = gross, so a naive Gross − WHT would not reconcile).
    // WHT is SPLIT by gross-up treatment (per-row wht_gross_up): a grossed-up investor's WHT is a
    // company expense (paid on top, NOT deducted — net = gross), a non-grossed-up investor's WHT is
    // deducted from the investor (net = gross − wht). A MIXED contract has both. Accumulating the two
    // separately lets the card show an honest breakdown that reconciles: Net = Gross − DEDUCTED wht
    // (the company-expense portion is shown but not subtracted). `net` uses the schedule's own
    // interest_amount, which is already per-investor gross-up-aware, so it's correct in every case.
    const isGrossUpRow = (e) => e.wht_gross_up === true || e.wht_gross_up === 'true';
    const interestTotals = schedule.reduce(
      (acc, entry) => {
        if (getFlowType(entry.narration) !== 'outflow') return acc;
        const wht = parseFloat(entry.wht_amount) || 0;
        acc.gross += parseFloat(entry.gross_interest_amount) || parseFloat(entry.interest_amount) || 0;
        acc.net += parseFloat(entry.interest_amount) || 0;
        if (isGrossUpRow(entry)) acc.whtCompany += wht;
        else acc.whtDeducted += wht;
        return acc;
      },
      { gross: 0, net: 0, whtDeducted: 0, whtCompany: 0 }
    );
    const grossInterest = interestTotals.gross;
    const netInterest = interestTotals.net;
    const whtDeducted = interestTotals.whtDeducted;   // deducted from the investor
    const whtCompany = interestTotals.whtCompany;     // company expense (grossed up)
    const whtTotal = whtDeducted + whtCompany;
    // Presentation: mixed = both a deducted and a company-expense portion exist; else uniform.
    const isMixedWht = whtDeducted > 0 && whtCompany > 0;
    const hasGrossUp = whtCompany > 0;
    // Gross contract amount = capital + gross interest (Capital + Interest).
    const capitalAmountNum = parseFloat(contract?.capital_amount) || 0;
    const grossContractAmount = capitalAmountNum + grossInterest;

    // Tenor (years + days) from the contract start (deposit_date) to maturity — matches the
    // visible "Start Date" row. Whole years first, then the leftover days.
    const tenorLabel = (() => {
      if (!contract?.deposit_date || !contract?.maturity_date) return null;
      const start = new Date(contract.deposit_date);
      const end = new Date(contract.maturity_date);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;
      const years = differenceInYears(end, start);
      const days = differenceInDays(end, addYears(start, years));
      const yearsPart = `${years} year${years === 1 ? '' : 's'}`;
      const daysPart = `${days} day${days === 1 ? '' : 's'}`;
      return `${yearsPart}, ${daysPart}`;
    })();

    // Investor display: multi-investor contracts carry no scalar investor_name (investors
    // live in the contract_investors junction), so derive the label from the fetched list,
    // flagging multi-investor with a count. Fall back to the scalar for single-investor.
    const isMultiInvestor =
      Boolean(contract?.is_multi_investor) || contractInvestors.length > 1;
    const investorLabel = isMultiInvestor
      ? 'Multiple investors'
      : (contractInvestors[0]?.investor_name || contract?.investor_name);

    // Reserve the docked preview pane whenever this contract approval has any proof
    // document(s) — the split stays stable and the pane shows a "select a document"
    // placeholder until the approver clicks an "Attached:" chip. When there are no proof
    // documents at all, the layout collapses to full-width single column (no empty pane).
    const proofDocIds = detailData?.proof_document_ids || item?.proof_document_ids || [];
    const hasProofPane = Array.isArray(proofDocIds) && proofDocIds.length > 0;
    const hasPreviewLoaded = Boolean(docActions.previewUrl);
    const activeProofDoc = proofDocs.find(d => d.id === docActions.previewDocId);
    const activeProofName = activeProofDoc?.file_name || 'Signed Document';
    // Friendly description of the active document from its document_type code (falls back to a
    // title-cased code, then to a generic label) — shown as the pane header above the filename.
    const activeProofDescription = documentTypeLabel(activeProofDoc?.document_type);

    return (
      <Box>
        {/* Contract detail tables + schedule summary sit in the left column beside the
            docked document pane; the wide amortization table renders full-width BELOW
            this row so it stays legible even when the pane is docked. */}
        <Grid container spacing={2}>
          <Grid size={hasProofPane ? { xs: 12, md: 4 } : { xs: 12 }}>
            <Box ref={setLeftColumnRef}>
        {schedule.length === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            No amortization schedule exists for this contract. Approval will be blocked until a schedule is generated.
          </Alert>
        )}
        {schedule.length > 0 && !terminalBalanceOk && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Schedule final running balance is {formatNumber(terminalBalanceForDisplay ?? 0, 2)} (tolerance {SCHEDULE_TERMINAL_BALANCE_TOLERANCE.toFixed(2)}). Approval is disabled until the schedule pays down to zero — reject this submission so the schedule clears, then regenerate before resubmitting.
          </Alert>
        )}
        {/* "Contract Details" heading is a DIRECT child of the left column Box (NOT inside the
            nested Grid below) so it sits at the same structural level as the preview pane's Paper
            header — the nested Grid's negative top margin (MUI spacing) would otherwise pull this
            heading up a few px and break alignment with the pane. Shares PANE_HEADING_SX with the
            pane header so their text tops pin. */}
        <Box sx={PANE_HEADING_SX}>
          <Typography variant="subtitle2" color="text.secondary">
            Contract Details
          </Typography>
        </Box>
        {/* Contract detail cards stack vertically (full width) within the left column so the
            docked document preview gets more horizontal room. */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <SummaryRow
                    label="Investor"
                    value={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <span>{investorLabel || '-'}</span>
                        {isMultiInvestor && (
                          <Chip label={contractInvestors.length} size="small" color="info" />
                        )}
                      </Box>
                    }
                  />
                  <SummaryRow label="Issuer" value={contract.issuer_name} />
                  <SummaryRow
                    label="Product"
                    value={contract.product_type && <Chip label={contract.product_type} size="small" />}
                  />
                  <SummaryRow
                    label="Capital Amount"
                    value={formatCurrencyDp(contract.capital_amount, currencyCode)}
                    highlight
                  />
                  <SummaryRow
                    label="Total Interest"
                    value={formatCurrencyDp(grossInterest, currencyCode)}
                  />
                  {hasWhtData && (
                    <>
                      {/* WHT split by gross-up treatment: mixed contracts show BOTH the
                          deducted-from-investor portion AND the company-expense (grossed-up) portion,
                          so the card reconciles (Net = Gross − deducted WHT). Uniform contracts show a
                          single line with the matching label. */}
                      {isMixedWht ? (
                        <>
                          <SummaryRow
                            label={<>Withholding Tax<br />Deducted from Investor</>}
                            value={formatCurrencyDp(whtDeducted, currencyCode)}
                          />
                          <SummaryRow
                            label={<>Withholding Tax<br />Company Expense</>}
                            value={formatCurrencyDp(whtCompany, currencyCode)}
                          />
                        </>
                      ) : (
                        <SummaryRow
                          label={hasGrossUp ? 'Withholding Tax (Company Expense)' : 'Withholding Tax'}
                          value={formatCurrencyDp(whtTotal, currencyCode)}
                        />
                      )}
                      <SummaryRow
                        label="Net Interest"
                        value={formatCurrencyDp(netInterest, currencyCode)}
                      />
                    </>
                  )}
                  <SummaryRow
                    label="Gross Contract Amount"
                    value={formatCurrencyDp(grossContractAmount, currencyCode)}
                    highlight
                  />
                  <SummaryRow label="Currency" value={currencyCode} />
                  {/* T117/T119: multi-currency visibility — only when the investor settles in a
                      different currency than the contract accrues in. */}
                  {contract.settlement_currency_code && contract.settlement_currency_code !== currencyCode && (
                    <>
                      <SummaryRow
                        label="Settlement Currency"
                        value={`${contract.settlement_currency_code} — capital & interest settle in ${contract.settlement_currency_code} (accrues in ${currencyCode})`}
                        highlight
                      />
                      {contract.settlement_fx_rate != null && (
                        <SummaryRow
                          label="Initial Capital Rate"
                          value={`${contract.settlement_currency_code}/${currencyCode} ${contract.settlement_fx_rate} — agreed for initial capital; interest & later settlements use the live rate`}
                        />
                      )}
                    </>
                  )}
                  <SummaryRow label="Rate Type" value={contract.rate_type} />
                  <SummaryRow
                    label="Interest Rate"
                    value={contract.interest_rate != null ? `${contract.interest_rate}%` : '-'}
                    highlight
                  />
                  {/* T3-068: rate-change effective basis (Variable/Linked only); cadence when On Specified Date. */}
                  {contract.rate_type === 'Variable' && (
                    <SummaryRow
                      label="Rate Change Effective"
                      value={formatRateChangeBasis(contract.rate_change_effective_basis)}
                    />
                  )}
                  {contract.rate_type === 'Variable' && contract.rate_change_effective_basis === 'specified_date' && (
                    <>
                      <SummaryRow label="Rate Change Frequency" value={contract.rate_change_frequency || '-'} />
                      <SummaryRow
                        label="First Rate Change Date"
                        value={contract.first_rate_change_date ? formatDateShort(contract.first_rate_change_date) : '-'}
                      />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Dates & Payment Structure
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <SummaryRow label="Start Date" value={formatDateShort(contract.deposit_date)} />
                  <SummaryRow
                    label="Funds Received"
                    value={contract.funds_received_date ? formatDateShort(contract.funds_received_date) : 'Same as start date'}
                  />
                  <SummaryRow label="Maturity Date" value={formatDateShort(contract.maturity_date)} highlight />
                  <SummaryRow label="Tenor" value={tenorLabel} />
                  <SummaryRow
                    label="Day Count Basis"
                    value={contract.day_count_basis ? `${contract.day_count_basis} days` : '365 days'}
                  />
                  <SummaryRow label="Interest Frequency" value={contract.interest_paid_frequency} />
                  <SummaryRow label="Capital Frequency" value={contract.capital_paid_frequency || 'At Maturity'} />
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Broker, Stamp Duty & Collateral if applicable */}
          {(contract.is_brokered || contract.has_collateral || (Array.isArray(contract.stamp_duties) && contract.stamp_duties.length > 0) || contract.stamp_duty) && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Broker, Stamp Duty & Collateral
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {contract.is_brokered && (
                      <>
                        <SummaryRow label="Broker" value={contract.broker_name} />
                        <SummaryRow
                          // T3-012 / T3-084: shared presentation — VAT breakdown when VAT
                          // applies, a single amount + "No-VAT" tag under the override.
                          label={contract.broker_fees != null
                            ? formatBrokerFee(contract, currencyCode).label
                            : 'Broker Fees'}
                          value={contract.broker_fees != null
                            ? (() => {
                                const fee = formatBrokerFee(contract, currencyCode);
                                return (
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    {fee.value}
                                    {fee.isNoVat && (
                                      <Chip label="No-VAT" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    )}
                                  </Box>
                                );
                              })()
                            : '-'}
                        />
                        {/* T3-084: the approve/reject gate is where a 0% VAT on a brokered fee
                            most needs explaining — without the reason the note is worthless. */}
                        {contract.broker_fee_vat_treatment === 'NO_VAT' && (
                          <SummaryRow
                            label="Broker Fee VAT Treatment"
                            value={`No-VAT (0%)${contract.broker_fee_settlement_currency_code ? ` — settled in ${contract.broker_fee_settlement_currency_code}` : ''}${contract.broker_fee_settled_in_other_ccy ? ' (different currency)' : ''}${contract.broker_fee_vat_note ? ` · ${contract.broker_fee_vat_note}` : ''}`}
                          />
                        )}
                      </>
                    )}
                    {/* T113: stamp duty is standalone + multi-row — one line per row,
                        shown whenever present (brokered or not). Falls back to the
                        legacy single column when the array is absent. */}
                    {(Array.isArray(contract.stamp_duties) && contract.stamp_duties.length > 0
                      ? contract.stamp_duties
                      : (contract.stamp_duty && contract.stamp_duty_fees
                          ? [{ reference: 'Stamp Duty', percentage: contract.stamp_duty_fees }]
                          : [])
                    ).map((sd, i) => (
                      <SummaryRow
                        key={sd.id ?? i}
                        label={sd.reference || 'Stamp Duty'}
                        value={`${formatRatePct(sd.percentage)}% (${currencyCode} ${formatNumber((parseFloat(contract.capital_amount) || 0) * parseFloat(sd.percentage) / 100)})`}
                      />
                    ))}
                    {contract.has_collateral && (
                      <>
                        <SummaryRow label="Collateral Provider" value={contract.collateral_provider_name} />
                        <SummaryRow label="Collateral Type" value={contract.collateral_type} />
                        <SummaryRow
                          label="Collateral Amount"
                          value={formatCurrencyDp(contract.collateral_amount, currencyCode)}
                        />
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
        {/* Impact Summary Metrics */}
        {summary && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Schedule Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Capital Amount
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrencyDp(contract.capital_amount, currencyCode)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Interest Rate
                    </Typography>
                    <Typography variant="h6">
                      {contract.interest_rate != null ? `${contract.interest_rate}%` : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total Interest
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrencyDp(grossInterest, currencyCode)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <Box sx={{ textAlign: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Schedule Entries
                    </Typography>
                    <Typography variant="h6">
                      {schedule.length}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Adhoc Events Summary */}
              {(adhocCapitalEvents.length > 0 || adhocInterestPayments.length > 0) && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Adhoc Events
                  </Typography>
                  <Grid container spacing={2}>
                    {adhocCapitalEvents.length > 0 && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                            Capital Events ({adhocCapitalEvents.length})
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {adhocCapitalEvents.map((evt, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>{formatDateShort(evt.event_date)}</TableCell>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>
                                    <Chip
                                      label={evt.event_type === 'withdrawal' ? 'Withdrawal' : 'Injection'}
                                      size="small"
                                      color={evt.event_type === 'withdrawal' ? 'warning' : 'success'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.5, border: 0, fontWeight: 'bold' }}>
                                    {formatCurrencyDp(evt.amount, currencyCode)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    )}
                    {adhocInterestPayments.length > 0 && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                            Interest Payment Dates ({adhocInterestPayments.length})
                          </Typography>
                          <Table size="small">
                            <TableBody>
                              {adhocInterestPayments.map((evt, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ py: 0.5, border: 0 }}>{formatDateShort(evt.event_date)}</TableCell>
                                  <TableCell sx={{ py: 0.5, border: 0, color: 'text.secondary' }}>
                                    {evt.description || 'Adhoc interest payment'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
            </Box>
          </Grid>

          {/* Docked document preview — approver compares the signed PDF against the
              contract assessment without the preview covering the modal. Renders only
              when a proof document has loaded (see the auto-load effect); the "Attached:"
              chips above the tabs swap which document is shown here. */}
          {hasProofPane && (
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper
                variant="outlined"
                sx={{
                  // Height mirrors the measured left column (via ResizeObserver) so the pane's
                  // bottom lines up with the last left-column card (Broker/Stamp Duty/Collateral).
                  // On xs the columns stack, so use a fixed height. Fall back to 480 until measured.
                  height: { xs: 480, md: leftColumnHeight ? `${leftColumnHeight}px` : 480 },
                  minHeight: 360,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    // Fixed-height header band matching the left column's "Contract Details" heading
                    // (see PANE_HEADING_SX) so the two column headers' text tops pin exactly.
                    ...PANE_HEADING_SX,
                  }}
                >
                  {/* One-line header: the document description (from its type). Filename is in the
                      hover tooltip. No Download button — the PDF viewer provides its own. */}
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    noWrap
                    title={hasPreviewLoaded ? `${activeProofDescription} — ${activeProofName}` : 'Document Preview'}
                  >
                    {hasPreviewLoaded ? activeProofDescription : 'Document Preview'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  {hasPreviewLoaded ? (
                    <DocumentPreviewPane
                      previewUrl={docActions.previewUrl}
                      previewType={docActions.previewType}
                      previewDocId={docActions.previewDocId}
                      downloadDocument={docActions.downloadDocument}
                      height="100%"
                      minHeight={0}
                      fitToWidth
                    />
                  ) : (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        p: 2,
                      }}
                    >
                      <PreviewIcon fontSize="large" color="disabled" />
                      <Typography variant="body2">Please select a document to preview</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Per-investor summary (multi-investor contracts) — collapsible, full-width, shown
            just above the amortization schedule so the approver sees each investor's capital
            allocation before reviewing the combined schedule. */}
        {contractInvestors.length > 1 && (
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box
              onClick={() => setInvestorSummaryOpen((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                Investors ({contractInvestors.length})
              </Typography>
              <Chip
                label={`Total ${formatCurrencyDp(
                  contractInvestors.reduce((s, inv) => s + (parseFloat(inv.capital_amount) || 0), 0),
                  currencyCode
                )}`}
                size="small"
                variant="outlined"
              />
              <IconButton size="small" aria-label={investorSummaryOpen ? 'Collapse investors' : 'Expand investors'}>
                {investorSummaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={investorSummaryOpen} timeout="auto" unmountOnExit>
              <TableContainer sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>#</TableCell>
                      <TableCell sx={headerCellSx}>Investor</TableCell>
                      <TableCell sx={headerCellSx} align="right">Capital</TableCell>
                      <TableCell sx={headerCellSx} align="right">Share %</TableCell>
                      <TableCell sx={headerCellSx} align="right">New Funds</TableCell>
                      <TableCell sx={headerCellSx} align="right">Rolled Funds</TableCell>
                      <TableCell sx={headerCellSx} align="right">WHT %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contractInvestors.map((inv, i) => (
                      <TableRow key={inv.investor_id ?? i} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                        <TableCell>{inv.sequence_number ?? i + 1}</TableCell>
                        <TableCell>
                          {inv.investor_name || `Investor ${inv.investor_id}`}
                          {inv.rolled_from_reference && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Rolled from {inv.rolled_from_user_reference || inv.rolled_from_reference}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrencyDp(inv.capital_amount, currencyCode)}</TableCell>
                        <TableCell align="right">
                          {inv.percentage != null ? `${formatRatePct(inv.percentage)}%` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {inv.new_funds_amount != null ? formatNumber(inv.new_funds_amount, 2) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {inv.rolled_funds_amount != null ? formatNumber(inv.rolled_funds_amount, 2) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {inv.wht_rate != null ? `${formatRatePct(inv.wht_rate)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        )}

        {/* Amortization Schedule Table - matches AmortizationScheduleTable columns (minus Status).
            Rendered full-width below the two-column row so all columns stay legible even when
            the document preview pane is docked. */}
        {schedule.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Amortization Schedule ({scheduleGroups
                ? `${scheduleGroups.length} periods, ${schedule.length} entries`
                : `${schedule.length} entries`})
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportAmortizationSchedule(schedule, contract)}
            >
              Export to Excel
            </Button>
          </Box>
        )}
        {schedule.length > 0 ? (
          // Full schedule, no pagination — the sticky header + scrollable container keeps
          // it navigable while showing every row for the approver to review in one pass.
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  {scheduleGroups && <TableCell sx={{ ...headerCellSx, width: 40 }} />}
                  <TableCell sx={headerCellSx}>Seq</TableCell>
                  <TableCell sx={headerCellSx}>Payment Date</TableCell>
                  {scheduleGroups && <TableCell sx={headerCellSx}>Investor</TableCell>}
                  <TableCell sx={headerCellSx} align="center">Flow</TableCell>
                  <TableCell sx={headerCellSx}>Narration</TableCell>
                  <TableCell sx={headerCellSx} align="right">Rate %</TableCell>
                  <TableCell sx={headerCellSx} align="right">Days</TableCell>
                  <TableCell sx={headerCellSx} align="right">Capital Amount</TableCell>
                  <TableCell sx={headerCellSx} align="right">Interest Amount</TableCell>
                  {hasWhtData && <TableCell sx={headerCellSx} align="right">Withholding Tax</TableCell>}
                  <TableCell sx={headerCellSx} align="right">Payment Amount</TableCell>
                  <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  <TableCell sx={headerCellSx} align="right">Outstanding Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduleGroups
                  ? // Grouped (multi-investor): one summary row per period, expandable to
                    // per-investor sub-rows. Sums are from the shared grouping util (no recompute).
                    scheduleGroups.map((group, gIdx) => {
                      const narration = group.narration || '';
                      const flowType = getFlowType(narration);
                      const flowLabel = getFlowLabel(narration);
                      const seq = gIdx + 1;
                      const isExpanded = !!expandedScheduleGroups[group.groupKey];
                      const isSingleEntry = group.investorCount === 1;
                      // Group total mirrors the flat row: capital + net interest.
                      const groupTotal = rowTotal(group);
                      return (
                        <React.Fragment key={group.groupKey}>
                          <TableRow
                            hover
                            sx={{
                              cursor: isSingleEntry ? 'default' : 'pointer',
                              // Row band resolved from type (adjustment > group header).
                              ...getNarrationRowSx(group.narration, { isGroup: true }),
                            }}
                            onClick={isSingleEntry ? undefined : () => toggleScheduleGroup(group.groupKey)}
                          >
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              {!isSingleEntry && (
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell><strong>{seq}</strong></TableCell>
                            <TableCell>{formatDateShort(group.payment_date)}</TableCell>
                            <TableCell>
                              {isSingleEntry ? (
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {group.entries[0].investor_name || '-'}
                                </Typography>
                              ) : (
                                <Chip label={`${group.investorCount} investors`} size="small" variant="outlined" color="primary" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={flowLabel.label} size="small" color={flowLabel.color} variant={flowLabel.sx ? 'outlined' : undefined} sx={{ minWidth: 40, fontWeight: 'bold', ...(flowLabel.sx || {}) }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={narration || 'N/A'} size="small" color={getNarrationColor(narration)} variant="outlined" />
                            </TableCell>
                            {/* Accrual rows only, matching AmortizationScheduleTable: a payment row
                                settles a balance and carries no rate of its own. Gating also removes
                                this table's divergence from the contract screen, where a stored 0
                                rendered as '0.00' here but '-' there. */}
                            <TableCell align="right">
                              {!isPeriodAccrual(narration) ? '-' : group._ratesDiffer ? 'Mixed' : (group.interest_rate != null ? parseFloat(group.interest_rate).toFixed(2) : '-')}
                            </TableCell>
                            <TableCell align="right">{group.days_in_period || '-'}</TableCell>
                            <TableCell align="right">{formatFlowAmount(group.capital_amount, flowType)}</TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {formatFlowAmount(group.interest_amount, flowType)}
                                {/* Σ calc-detail only on single-entry groups: a summed multi-investor
                                    period has no single audit record — the icon lives on each
                                    per-investor sub-row instead (below). */}
                                {isSingleEntry && group.entries[0]?.id && narration === 'Interest Accrual' && (
                                  <Tooltip title="View calculation detail" arrow>
                                    <FunctionsIcon
                                      sx={{ fontSize: 14, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                      onClick={(e) => { e.stopPropagation(); handleAuditClick(e, group.entries[0]); }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            {hasWhtData && (
                              <TableCell align="right">
                                {renderWhtCell(group.wht_amount, group.narration)}
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <strong>{formatFlowAmount(group.payment_amount, flowType)}</strong>
                            </TableCell>
                            <TableCell align="right">
                              {groupTotal === 0 ? '-' : formatFlowAmount(groupTotal, flowType)}
                            </TableCell>
                            {/* Sum of the period's submitted per-investor tranche balances — the
                                engine writes outstanding_balance per investor, so the contract-level
                                balance is the sum of the concurrent tranches. Values come straight
                                from the submitted snapshot; nothing is recomputed. */}
                            <TableCell align="right">
                              <strong>{formatNumber(group.outstanding_balance, 2)}</strong>
                            </TableCell>
                          </TableRow>

                          {/* Per-investor sub-rows (when expanded) */}
                          {isExpanded && !isSingleEntry && group.entries.map((entry, eIdx) => {
                            const eFlow = getFlowType(entry.narration);
                            return (
                              <TableRow
                                key={entry.id || `${group.groupKey}-${eIdx}`}
                                sx={{
                                  // Expanded sub-rows keep their striping. (Adjustment rows are
                                  // marked by their maroon chips, not a row band.)
                                  backgroundColor: eIdx % 2 === 0 ? '#fafcff' : '#ffffff',
                                  borderLeft: '3px solid #1976d2',
                                  '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' },
                                }}
                              >
                                <TableCell sx={{ width: 40 }} />
                                <TableCell />
                                <TableCell />
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, pl: 0.5 }}>
                                    {entry.investor_name || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                {/* Accrual rows only — see the group row above. */}
                                <TableCell align="right">
                                  {isPeriodAccrual(entry.narration) && entry.interest_rate != null
                                    ? parseFloat(entry.interest_rate).toFixed(2)
                                    : '-'}
                                </TableCell>
                                <TableCell />
                                <TableCell align="right">{formatFlowAmount(entry.capital_amount, eFlow)}</TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {formatFlowAmount(entry.interest_amount, eFlow)}
                                    {entry.id && entry.narration === 'Interest Accrual' && (
                                      <Tooltip title="View calculation detail" arrow>
                                        <FunctionsIcon
                                          sx={{ fontSize: 14, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                          onClick={(e) => { e.stopPropagation(); handleAuditClick(e, entry); }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                {hasWhtData && (
                                  <TableCell align="right">
                                    {renderWhtCell(entry.wht_amount, entry.narration)}
                                  </TableCell>
                                )}
                                <TableCell align="right">{formatFlowAmount(entry.payment_amount, eFlow)}</TableCell>
                                <TableCell align="right">
                                  {(() => {
                                    const total = rowTotal(entry);
                                    if (total === 0) return '-';
                                    return formatFlowAmount(total, eFlow);
                                  })()}
                                </TableCell>
                                {/* This investor's own submitted tranche balance. The sub-rows of a
                                    period add up to the group row's balance. */}
                                <TableCell align="right">
                                  {Number.isFinite(parseFloat(entry.outstanding_balance))
                                    ? formatNumber(parseFloat(entry.outstanding_balance), 2)
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })
                  : // Flat (single-investor, or pre-T3-055 snapshots without investor_id).
                    schedule.map((entry, idx) => {
                  const narration = entry.narration || '';
                  const flowType = getFlowType(narration);
                  const flowLabel = getFlowLabel(narration);
                  const seq = idx + 1;

                  return (
                    <TableRow
                      key={seq}
                      sx={getNarrationRowSx(narration)}
                    >
                      <TableCell>{seq}</TableCell>
                      <TableCell>{formatDateShort(entry.payment_date)}</TableCell>
                      <TableCell align="center">
                        <Chip label={flowLabel.label} size="small" color={flowLabel.color} variant={flowLabel.sx ? 'outlined' : undefined} sx={{ minWidth: 40, fontWeight: 'bold', ...(flowLabel.sx || {}) }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={narration || 'N/A'} size="small" color={getNarrationColor(narration)} variant="outlined" />
                      </TableCell>
                      {/* Accrual rows only — see the grouped row above. */}
                      <TableCell align="right">
                        {isPeriodAccrual(narration) && entry.interest_rate != null
                          ? parseFloat(entry.interest_rate).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{entry.days_in_period || '-'}</TableCell>
                      <TableCell align="right">
                        {formatFlowAmount(entry.capital_amount, flowType)}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {formatFlowAmount(entry.interest_amount, flowType)}
                          {entry.id && narration === 'Interest Accrual' && (
                            <Tooltip title="View calculation detail" arrow>
                              <FunctionsIcon
                                sx={{ fontSize: 14, color: 'action.disabled', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                onClick={(e) => handleAuditClick(e, entry)}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      {hasWhtData && (
                        <TableCell align="right">
                          {renderWhtCell(entry.wht_amount, narration)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <strong>{formatFlowAmount(entry.payment_amount, flowType)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const total = rowTotal(entry);
                          if (total === 0) return '-';
                          return formatFlowAmount(total, flowType);
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatNumber(parseFloat(entry.outstanding_balance) || 0, 2)}</strong>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">No amortization schedule available for this contract.</Alert>
        )}

        {/* Calculation Detail (Σ) popover — shared with the contract wizard / detail schedule.
            Only opens for snapshot rows carrying an id (T3-072+); older frozen snapshots omit
            it, so the Σ icon simply does not render on those rows. */}
        <CalculationDetailPopover
          anchorEl={auditAnchorEl}
          onClose={handleAuditClose}
          entryId={auditEntryId}
          contractId={item?.entity_id}
        />
      </Box>
    );
  };

  // Field configuration for verification snapshot rendering
  const SNAPSHOT_FIELD_CONFIG = {
    capital_receipt: [
      { key: 'investor_name',       label: 'Investor',            format: 'text' },
      { key: 'contract_reference',  label: 'Contract Reference',  format: 'text', highlight: true },
      { key: 'amount',              label: 'Amount',              format: 'currency' },
      { key: 'received_date',       label: 'Received Date',       format: 'date' },
      { key: 'reference',           label: 'External Reference',  format: 'text', highlight: true },
      { key: 'payment_method',      label: 'Payment Method',      format: 'text' },
      { key: 'currency_code',       label: 'Currency',            format: 'text' },
      { key: 'notes',               label: 'Notes',               format: 'text' },
    ],
    payment_match: [
      { key: 'contract_reference',  label: 'Contract Reference',  format: 'text', highlight: true },
      { key: 'investor_name',       label: 'Investor',            format: 'text' },
      { key: 'payment_amount',      label: 'Payment Amount',      format: 'currency' },
      { key: 'payment_date',        label: 'Payment Date',        format: 'date' },
      { key: 'payment_reference',   label: 'External Reference',  format: 'text', highlight: true },
      { key: 'payment_type',        label: 'Payment Type',        format: 'text' },
      { key: 'expected_amount',     label: 'Expected Amount',     format: 'currency' },
      { key: 'schedule_date',       label: 'Schedule Date',       format: 'date' },
      { key: 'schedule_narration',  label: 'Schedule Entry Type', format: 'text' },
      { key: 'currency_code',       label: 'Currency',            format: 'text' },
      { key: 'status',              label: 'Status',              format: 'text' },
    ],
  };

  const SUPPRESSED_KEYS = new Set([
    'receipt_id', 'investor_id', 'contract_id', 'payment_id', 'schedule_id',
    'schedule_sequence',
    // The full submitted schedule (after_snapshot.schedule, T118/T3-055) is an array of ~46 row
    // objects meant to be shown as a TABLE (renderContractApprovalPreview), never as an inline text
    // field. When a contract_approval degrades to this generic renderer (e.g. the /contracts/:id fetch
    // 403'd), stringifying the array would print "[object Object],[object Object],…", so suppress it.
    'schedule',
  ]);

  const humanizeKey = (key) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Object-safe coercion for the generic Before/After fields. A plain String() on an array/object
  // yields "[object Object]", so summarise non-primitives instead. Mirrors formatAuditValue in
  // ReportsPage.js (typeof === 'object' -> JSON.stringify), with an array-length shortcut so a stray
  // array reads as "N item(s)" rather than a dump.
  const formatSnapshotValue = (value) => {
    if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
    if (value !== null && typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const renderSnapshotFields = (snapshot, changeData, changeType) => {
    if (!snapshot && !changeData) return null;
    const config = SNAPSHOT_FIELD_CONFIG[changeType];
    const merged = { ...(changeData || {}), ...(snapshot || {}) };
    const renderedKeys = new Set();

    const fmtValue = (value, fmt) => {
      if (value === null || value === undefined || value === '') return null;
      switch (fmt) {
        case 'currency': return formatCurrencyDp(value, merged.currency_code);
        case 'date': return formatDateShort(value);
        case 'number': return formatSnapshotValue(value);
        default: return formatSnapshotValue(value);
      }
    };

    const fields = [];

    if (config) {
      for (const field of config) {
        renderedKeys.add(field.key);
        const val = fmtValue(merged[field.key], field.format);
        if (val == null) continue;
        fields.push(
          <Typography key={field.key} variant="body2" sx={field.highlight ? { fontWeight: 'medium' } : undefined}>
            <strong>{field.label}:</strong> {val}
          </Typography>
        );
      }
    }

    // Generic fallback for unconfigured keys
    for (const [key, value] of Object.entries(snapshot || {})) {
      if (renderedKeys.has(key) || SUPPRESSED_KEYS.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;
      fields.push(
        <Typography key={key} variant="body2">
          <strong>{humanizeKey(key)}:</strong> {formatSnapshotValue(value)}
        </Typography>
      );
    }

    return fields.length > 0 ? fields : null;
  };

  // Render generic before/after comparison (non-contract types)
  const renderGenericComparison = () => {
    const before = currentItem.before_snapshot;
    const after = currentItem.after_snapshot;
    const changeData = currentItem.change_data;

    // Show amendment details for capital/interest amendments
    const isCapitalAmendment = ['adhoc_capital_withdrawal', 'adhoc_capital_injection'].includes(currentItem.change_type);
    const isInterestAmendment = currentItem.change_type === 'adhoc_interest_payment';
    const isFinalisation = currentItem.change_type === 'contract_finalisation';
    // Reserve payout has a rich per-investor comparison only when the enriched
    // snapshot is present (submits after the enrichment change). Older/in-flight
    // approvals carry a flat { status } snapshot and fall back to the generic view.
    const isReservePayout = currentItem.change_type === 'reserve_payout';
    const hasReservePayoutBreakdown = isReservePayout && Array.isArray(before?.investors);

    // Build per-investor reserve-payout rows: the full "before" reserve picture
    // joined to the projected "after" (pay-out-now + remaining-after) for the
    // investors actually being paid. Mirrors what the submitter saw.
    const reservePayoutRows = (() => {
      if (!hasReservePayoutBreakdown) return null;
      const afterByInvestor = new Map(
        (Array.isArray(after?.investors) ? after.investors : []).map((r) => [Number(r.investor_id), r])
      );
      return before.investors.map((r) => {
        const a = afterByInvestor.get(Number(r.investor_id));
        const remaining = parseFloat(r.remaining) || 0;
        const payout = a ? (parseFloat(a.payout_amount) || 0) : 0;
        return {
          investor_id: r.investor_id,
          investor_name: r.investor_name,
          reserve_amount: parseFloat(r.reserve_amount) || 0,
          already_rolled: parseFloat(r.already_rolled) || 0,
          paid_out_amount: parseFloat(r.paid_out_amount) || 0,
          remaining,
          payout_amount: payout,
          remaining_after: a ? (parseFloat(a.remaining_after) || 0) : remaining,
        };
      });
    })();

    const reservePayoutTotals = (() => {
      if (!reservePayoutRows) return null;
      return reservePayoutRows.reduce(
        (acc, r) => ({
          reserve_amount: acc.reserve_amount + r.reserve_amount,
          already_rolled: acc.already_rolled + r.already_rolled,
          paid_out_amount: acc.paid_out_amount + r.paid_out_amount,
          remaining: acc.remaining + r.remaining,
          payout_amount: acc.payout_amount + r.payout_amount,
          remaining_after: acc.remaining_after + r.remaining_after,
        }),
        { reserve_amount: 0, already_rolled: 0, paid_out_amount: 0, remaining: 0, payout_amount: 0, remaining_after: 0 }
      );
    })();

    const reservePayoutCurrency = before?.currency_code || after?.currency_code || contractData?.currency_code || '';

    // Build per-investor projected rows for finalisations by joining the
    // summary (capital, interest, WHT, net) with the reviewer's reserve/payout
    // split from change_data.investor_allocations.
    const finalisationRows = (() => {
      if (!isFinalisation || !Array.isArray(finalisationSummary)) return null;
      const allocations = Array.isArray(changeData?.investor_allocations)
        ? changeData.investor_allocations
        : [];
      const allocByInvestor = new Map(
        allocations.map((a) => [Number(a.investor_id), a])
      );
      return finalisationSummary.map((inv) => {
        const alloc = allocByInvestor.get(Number(inv.investor_id)) || {};
        return {
          investor_id: inv.investor_id,
          investor_name: inv.investor_name,
          original_capital: parseFloat(inv.original_capital) || 0,
          accrued_interest: parseFloat(inv.accrued_interest) || 0,
          wht_amount: parseFloat(inv.wht_amount) || 0,
          net_value: parseFloat(inv.net_value) || 0,
          reserve_amount: parseFloat(alloc.reserve_amount) || 0,
          payout_amount: parseFloat(alloc.payout_amount) || 0,
        };
      });
    })();

    const finalisationTotals = (() => {
      if (!finalisationRows) return null;
      return finalisationRows.reduce(
        (acc, r) => ({
          original_capital: acc.original_capital + r.original_capital,
          accrued_interest: acc.accrued_interest + r.accrued_interest,
          wht_amount: acc.wht_amount + r.wht_amount,
          net_value: acc.net_value + r.net_value,
          reserve_amount: acc.reserve_amount + r.reserve_amount,
          payout_amount: acc.payout_amount + r.payout_amount,
        }),
        { original_capital: 0, accrued_interest: 0, wht_amount: 0, net_value: 0, reserve_amount: 0, payout_amount: 0 }
      );
    })();

    return (
      <Grid container spacing={2}>
        {/* Amendment Details */}
        {(isCapitalAmendment || isInterestAmendment) && changeData && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Amendment Details</Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1}>
                {changeData.event_type && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {changeData.event_type === 'withdrawal' ? 'Capital Withdrawal' : changeData.event_type === 'injection' ? 'Capital Injection' : 'Interest Payment'}
                    </Typography>
                  </Grid>
                )}
                {changeData.event_date && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Event Date</Typography>
                    <Typography variant="body2">{changeData.event_date}</Typography>
                  </Grid>
                )}
                {changeData.amount && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatNumber(parseFloat(changeData.amount), 2)}
                    </Typography>
                  </Grid>
                )}
                {changeData.penalty_amount && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Typography variant="caption" color="text.secondary">Penalty</Typography>
                    <Typography variant="body2" color="error.main" fontWeight="medium">
                      {formatNumber(parseFloat(changeData.penalty_amount), 2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {changeData.motivation && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Motivation</Typography>
                  <Typography variant="body2">{changeData.motivation}</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Finalisation details: projected per-investor allocations */}
        {isFinalisation && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Finalisation Details</Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {changeData?.finalisation_date && (
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Finalisation Date</Typography>
                    <Typography variant="body2">{changeData.finalisation_date}</Typography>
                  </Grid>
                )}
                {changeData?.reason && (
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{changeData.reason}</Typography>
                  </Grid>
                )}
              </Grid>

              {finalisationRows && finalisationRows.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Investor</TableCell>
                        <TableCell sx={headerCellSx} align="right">Capital</TableCell>
                        <TableCell sx={headerCellSx} align="right">Interest</TableCell>
                        <TableCell sx={headerCellSx} align="right">WHT</TableCell>
                        <TableCell sx={headerCellSx} align="right">Net</TableCell>
                        <TableCell sx={headerCellSx} align="right">Reserve</TableCell>
                        <TableCell sx={headerCellSx} align="right">Payout</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {finalisationRows.map((r) => (
                        <TableRow key={r.investor_id}>
                          <TableCell>{r.investor_name}</TableCell>
                          <TableCell align="right">{formatNumber(r.original_capital, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.accrued_interest, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.wht_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.net_value, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.payout_amount, 2)}</TableCell>
                        </TableRow>
                      ))}
                      {finalisationTotals && (
                        <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'grey.50' } }}>
                          <TableCell>Total</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.original_capital, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.accrued_interest, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.wht_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.net_value, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(finalisationTotals.payout_amount, 2)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : finalisationSummary === null ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading projected allocations…</Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  Could not load projected per-investor allocations for this finalisation.
                </Alert>
              )}
            </Paper>
          </Grid>
        )}

        {/* Reserve payout details: per-investor reserve before -> projected after */}
        {hasReservePayoutBreakdown && (
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Reserve Payout Details{reservePayoutCurrency ? ` (${reservePayoutCurrency})` : ''}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {changeData?.payout_date && (
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">Payout Date</Typography>
                    <Typography variant="body2">{changeData.payout_date}</Typography>
                  </Grid>
                )}
                {changeData?.reason && (
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                    <Typography variant="body2">{changeData.reason}</Typography>
                  </Grid>
                )}
              </Grid>

              {reservePayoutRows && reservePayoutRows.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Investor</TableCell>
                        <TableCell sx={headerCellSx} align="right">Reserve</TableCell>
                        <TableCell sx={headerCellSx} align="right">Already Rolled</TableCell>
                        <TableCell sx={headerCellSx} align="right">Already Paid Out</TableCell>
                        <TableCell sx={headerCellSx} align="right">Remaining (before)</TableCell>
                        <TableCell sx={headerCellSx} align="right">Pay Out Now</TableCell>
                        <TableCell sx={headerCellSx} align="right">Remaining (after)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reservePayoutRows.map((r) => (
                        <TableRow key={r.investor_id}>
                          <TableCell>{r.investor_name}</TableCell>
                          <TableCell align="right">{formatNumber(r.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.already_rolled, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.paid_out_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(r.remaining, 2)}</TableCell>
                          <TableCell align="right" sx={{ color: r.payout_amount > 0 ? 'warning.main' : undefined, fontWeight: r.payout_amount > 0 ? 'medium' : undefined }}>
                            {formatNumber(r.payout_amount, 2)}
                          </TableCell>
                          <TableCell align="right">{formatNumber(r.remaining_after, 2)}</TableCell>
                        </TableRow>
                      ))}
                      {reservePayoutTotals && (
                        <TableRow sx={{ '& td': { fontWeight: 'bold', bgcolor: 'grey.50' } }}>
                          <TableCell>Total</TableCell>
                          <TableCell align="right">{formatNumber(reservePayoutTotals.reserve_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(reservePayoutTotals.already_rolled, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(reservePayoutTotals.paid_out_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(reservePayoutTotals.remaining, 2)}</TableCell>
                          <TableCell align="right" sx={{ color: 'warning.main' }}>{formatNumber(reservePayoutTotals.payout_amount, 2)}</TableCell>
                          <TableCell align="right">{formatNumber(reservePayoutTotals.remaining_after, 2)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No investors are being paid out in this request.</Alert>
              )}
            </Paper>
          </Grid>
        )}

        {/* Before/After comparison
            Skipped entirely for finalisations: the Finalisation Details panel
            above is the comparison, and the "Before" snapshot only duplicates
            contract metadata already shown elsewhere in the modal.
            Skipped for reserve payouts WITH a rich snapshot: the Reserve Payout
            Details panel above is the comparison. Flat/legacy reserve-payout
            snapshots (no .investors) still fall through to the generic cards. */}

        {/* Rich current-vs-projected comparison — same view the SUBMITTER saw.
            Rendered when the stored after_snapshot carries the full schedules
            (capital/interest/extension submits persist this at submit time).
            Falls back to the Before/After snapshot cards below otherwise. */}
        {after && Array.isArray(after.current_schedule) && Array.isArray(after.projected_schedule) ? (
          <Grid size={12}>
            <ImpactSummaryCards
              currentInterest={after.current?.total_interest}
              proposedInterest={after.projected?.total_interest}
              difference={after.interest_diff}
              percentageChange={after.current?.total_interest
                ? (after.interest_diff / Math.abs(after.current.total_interest)) * 100
                : null}
              currentWht={after.current?.total_wht}
              proposedWht={after.projected?.total_wht}
              currencyCode={contractData?.currency_code || 'ZAR'}
              dp={dp}
              proposedLabel="Projected Total Interest"
            />
            <Box sx={{ mt: 2 }}>
              <ScheduleComparisonTable
                rows={buildComparisonRows(after.current_schedule, after.projected_schedule, {
                  calcType: after.interest_calc_type,
                  compoundingFrequency: after.compounding_frequency,
                  dayCountBasis: after.day_count_basis,
                  dp,
                })}
                isIncrease={(after.interest_diff || 0) >= 0}
                dp={dp}
                currentLabel="Current"
                proposedLabel="Projected"
                changeTypeLabel={changeTypeLabel(currentItem).replace(/\b\w/g, (ch) => ch.toUpperCase())}
                currentVersion={after.current_version ?? null}
                nextVersion={after.next_version ?? null}
              />
            </Box>
          </Grid>
        ) : isFinalisation || hasReservePayoutBreakdown ? null : !before && !after ? (
          <Grid size={12}>
            <Alert severity="info">
              No preview comparison available for this change type.
            </Alert>
          </Grid>
        ) : (
          <>
        {/* Before */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
            <Typography variant="subtitle2" color="error.main" gutterBottom>
              Before
            </Typography>
            <Divider sx={{ my: 1 }} />
            {before ? (
              <Box>
                {renderSnapshotFields(before, changeData, currentItem.change_type) || (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No previous state
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* After */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              After
            </Typography>
            <Divider sx={{ my: 1 }} />
            {after ? (
              <Box>
                {renderSnapshotFields(after, changeData, currentItem.change_type) || (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No projected state
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Changes Summary */}
        {after?.changes_summary && after.changes_summary.length > 0 && (
          <Grid size={12}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Changes Summary:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {after.changes_summary.map((change, idx) => (
                  <li key={idx}>{change}</li>
                ))}
              </ul>
            </Alert>
          </Grid>
        )}
          </>
        )}
      </Grid>
    );
  };

  // Render verification-specific detail view (replaces Before/After for verifications)
  const renderVerificationDetails = () => {
    const after = currentItem.after_snapshot;
    const changeData = currentItem.change_data;
    const isVerification = currentItem.category === 'verification';
    const requestedBy = currentItem.requested_by?.toString().toLowerCase().trim();
    const isSubmitter = requestedBy && (
      requestedBy === currentUserEmail?.toLowerCase().trim() ||
      requestedBy === currentUserId?.toString()
    );
    const canUpload = isVerification && currentItem.status === 'queried' && isSubmitter;

    return (
      <Box>
        {/* Verification Details Card */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Verification Details</Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1}>
            {renderSnapshotFields(after, changeData, currentItem.change_type)?.map((field, idx) => (
              <Grid key={idx} size={{ xs: 12, sm: 6 }}>
                {field}
              </Grid>
            )) || (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">No details available</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Proof Documents */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Proof Documents</Typography>
          <Divider sx={{ my: 1 }} />
          {detailData?.proof_document_ids && detailData.proof_document_ids.length > 0 ? (
            <Grid container spacing={1} sx={{ mb: canUpload ? 2 : 0 }}>
              {detailData.proof_document_ids.map((docId, idx) => {
                const docMeta = proofDocs.find(d => d.id === docId);
                return (
                  <Grid size={6} key={docId}>
                    <Chip
                      icon={<PreviewIcon />}
                      label={docMeta?.file_name || `Document ${idx + 1}`}
                      onClick={() => docActions.openPreview(docId)}
                      clickable
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: canUpload ? 2 : 0 }}>
              No documents attached
            </Typography>
          )}

          {/* Upload new document (submitter on queried items). Drag-drop OR click
              both upload immediately (this surface auto-uploads on select). */}
          {canUpload && (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Attach additional document{verificationDocRequired ? ' (Required)' : ' (Optional)'}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <FileDropZone
                  file={null}
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                  dense
                  promptText="Choose a file, or drag and drop"
                  onFile={async (file) => {
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('document_type', 'proof_of_verification');
                      if (changeData?.contract_id) formData.append('contract_id', changeData.contract_id);
                      if (changeData?.investor_id) formData.append('investor_id', changeData.investor_id);
                      const uploadRes = await apiUpload('/admin/documents/upload', formData);
                      if (uploadRes?.data?.id) {
                        await apiPost(`/approvals/${item.id}/documents`, {
                          document_id: uploadRes.data.id,
                        });
                        // Reload details and document names
                        loadDetails();
                        reloadProofDocs();
                      }
                    } catch (err) {
                      logger.error('Error uploading document', { message: err.message });
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  // Render comparison - delegates to rich preview for contract approvals
  const renderComparison = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (item.change_type === 'contract_approval' && contractData) {
      return renderContractApprovalPreview();
    }

    if (item.category === 'verification') {
      return renderVerificationDetails();
    }

    // A contract_approval reaching here means the contract record failed to load (e.g. the
    // /contracts/:id fetch 403'd on a stale tenant context) — the rich Contract Assessment view
    // needs contractData and can't be built. Rather than silently dropping to the raw Before/After
    // diff, tell the user what happened and that a re-open retries (which is what resolves it).
    if (item.change_type === 'contract_approval') {
      return (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Contract details could not be loaded — showing a summary only. Close and re-open this
            request to retry.
          </Alert>
          {renderGenericComparison()}
        </>
      );
    }

    return renderGenericComparison();
  };

  // Render history timeline
  const renderHistory = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (history.length === 0) {
      return (
        <Alert severity="info">No history available.</Alert>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status Change</TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  <Chip
                    label={entry.action}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{entry.action_by}</TableCell>
                <TableCell>{formatDateTime(entry.action_at)}</TableCell>
                <TableCell>
                  {entry.status_before && (
                    <>
                      <Chip label={entry.status_before} size="small" sx={{ mr: 0.5 }} />
                      {' → '}
                    </>
                  )}
                  <Chip
                    label={entry.status_after}
                    size="small"
                    color={getStatusColor(entry.status_after)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                    {entry.comments || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={['contract_approval', 'adhoc_capital_withdrawal', 'adhoc_capital_injection', 'adhoc_interest_payment', 'contract_extension', 'contract_rollover'].includes(item?.change_type) ? 'xl' : 'lg'}
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {item.entity_reference || `#${item.entity_id}`}
              {/* External (user-supplied) reference, shown to the right of the system ref in bold. */}
              {contractData?.user_reference && (
                <Typography component="span" variant="h6" sx={{ ml: 1.5, fontWeight: 700 }}>
                  {contractData.user_reference}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {changeTypeLabel(item)} - {item.entity_type}
            </Typography>
          </Box>
          <Chip
            label={item.status.replace(/_/g, ' ')}
            color={getStatusColor(item.status)}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Warning if user cannot approve (creator/approver separation) — all change types */}
        {canApproveData && !canApproveData.can_approve && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Cannot Approve
            </Typography>
            <Typography variant="body2">
              {canApproveData.reason}
            </Typography>
            {item.change_type === 'contract_approval' && canApproveData.created_by && (
              <Typography variant="caption" color="text.secondary">
                Contract created by: {canApproveData.created_by}
              </Typography>
            )}
          </Alert>
        )}

        {/* Summary */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {item.change_summary}
        </Alert>

        {/* Query alert for queried items */}
        {item.status === 'queried' && item.reviewed_by && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Queried by {item.reviewed_by}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {item.review_comments || 'No comments provided'}
            </Typography>
            {item.reviewed_at && (
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(item.reviewed_at)}
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Requested By
            </Typography>
            <Typography variant="body2">{item.requested_by}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Requested At
            </Typography>
            <Typography variant="body2">{formatDateTime(item.requested_at)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Typography variant="body2">
              <Chip label={item.priority} size="small" />
            </Typography>
          </Grid>
        </Grid>

        {/* Proof documents (non-verification items only; verifications show docs in their detail view) */}
        {item.category !== 'verification' && item.proof_document_ids && item.proof_document_ids.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AttachmentIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Attached:
              </Typography>
            </Box>
            <Grid container spacing={1}>
              {item.proof_document_ids.map((docId, idx) => {
                const docMeta = itemProofDocs.find(d => d.id === docId);
                // Highlight the chip whose document is currently shown (docked pane
                // for contract approvals, pop-up dialog otherwise).
                const isActive = docActions.previewDocId === docId;
                return (
                  <Grid size={6} key={docId}>
                    <Chip
                      icon={<PreviewIcon />}
                      label={docMeta?.file_name || `Document ${idx + 1}`}
                      onClick={() => docActions.openPreview(docId)}
                      clickable
                      size="small"
                      color={isActive ? 'primary' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                      sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Tabs for details */}
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={item.change_type === 'contract_approval' ? <AccountBalanceIcon fontSize="small" /> : <CompareIcon fontSize="small" />}
            iconPosition="start"
            label={item.change_type === 'contract_approval' ? 'Contract Assessment' : item.category === 'verification' ? 'Verification Details' : 'Before / After'}
          />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label="History"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {renderComparison()}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {renderHistory()}
        </TabPanel>

        {/* Reviewer comments */}
        {item.reviewed_by && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Review Comments
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">
                <strong>{item.reviewed_by}</strong> ({formatDateTime(item.reviewed_at)}):
              </Typography>
              <Typography variant="body2">
                {item.review_comments || 'No comments'}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Action input */}
        {availableActions.length > 0 && (() => {
          // A reason-class action (reject/query/reverse/respond) needs the comment; approve/verify
          // do NOT (approve stays optional). The comment is only genuinely MANDATORY when there is
          // no optional escape hatch — i.e. reject/respond is the ONLY thing you can do (self-approval
          // → reject-only; queried verification → respond-only). When Approve is also available the
          // field is optional (you can approve with it empty); we still NOTE that rejecting needs a
          // reason, and the Reject button's own tooltip enforces it at click time.
          const reasonAction = availableActions.some((a) =>
            ['reject', 'query', 'reverse', 'respond'].includes(a));
          const hasOptionalAction = availableActions.some((a) => ['approve', 'verify'].includes(a));
          const reasonRequired = reasonAction && !hasOptionalAction;
          const rejectOnly = availableActions.length === 1 && availableActions[0] === 'reject';
          const missing = reasonRequired && !comments.trim();
          return (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                multiline
                rows={2}
                required={reasonRequired}
                error={missing}
                label={reasonRequired ? (rejectOnly ? 'Reason for rejection' : 'Reason') : 'Comments'}
                placeholder={reasonRequired
                  ? 'Enter the reason for rejecting this contract...'
                  : 'Add comments for your action...'}
                helperText={reasonAction ? 'A reason is required to reject.' : ' '}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Box>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        {availableActions.includes('reject') && (
          <Tooltip title={!comments.trim() ? 'Enter a comment to reject.' : ''}>
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => onAction('reject', comments)}
                disabled={!comments.trim()}
              >
                Reject
              </Button>
            </span>
          </Tooltip>
        )}
        {availableActions.includes('reverse') && (
          <Tooltip title={!comments.trim() ? 'Enter a comment to reverse.' : ''}>
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ReverseIcon />}
                onClick={() => onAction('reverse', comments)}
                disabled={!comments.trim()}
              >
                Reverse
              </Button>
            </span>
          </Tooltip>
        )}
        {availableActions.includes('query') && (
          <Tooltip title={!comments.trim() ? 'Enter a comment to query.' : ''}>
            <span>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<QueryIcon />}
                onClick={() => onAction('query', comments)}
                disabled={!comments.trim()}
              >
                Query
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Spacer: physically separates destructive/intermediate actions from
            positive actions (Approve/Verify/Respond) to reduce misclick risk. */}
        <Box sx={{ flex: 1 }} />

        {availableActions.includes('respond') && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={() => onAction('respond', comments)}
            disabled={!comments.trim()}
          >
            Respond
          </Button>
        )}
        {availableActions.includes('verify') && (
          <Tooltip
            title={
              verificationDocRequired && (!detailDocIds || detailDocIds.length === 0)
                ? 'A verification document must be attached before this item can be verified.'
                : ''
            }
          >
            <span>
              <Button
                color="success"
                variant="contained"
                startIcon={<VerifiedIcon />}
                onClick={() => onAction('verify', comments)}
                disabled={verificationDocRequired && (!detailDocIds || detailDocIds.length === 0)}
              >
                Verify
              </Button>
            </span>
          </Tooltip>
        )}
        {availableActions.includes('approve') && (
          <Tooltip
            title={
              approveBlockedForBalance
                ? `Schedule final balance is ${formatNumber(terminalBalanceForDisplay ?? 0, 2)} — must be within ${SCHEDULE_TERMINAL_BALANCE_TOLERANCE.toFixed(2)} of zero. Reject and regenerate the schedule.`
                : ''
            }
          >
            <span>
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={() => onAction('approve', comments)}
                disabled={approveBlockedForBalance}
              >
                Approve
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>

      {/* Document Preview Dialog — pop-up for every flow EXCEPT contract approvals,
          which show the document in the docked side-pane instead. Both read the same
          docActions state, so mounting the dialog unconditionally would double-render
          the preview on top of the docked pane for contract approvals. */}
      {item?.change_type !== 'contract_approval' && (
        <DocumentPreviewDialog {...docActions} />
      )}
    </Dialog>
  );
}

export default ApprovalDetailModal;
